/**
 * StatusBadge Component
 *
 * A unified, reusable badge component for displaying status indicators
 * across the application. Works with multiple status domains:
 * - Request status
 * - Practice status
 * - Redemption status (cashback)
 * - Generic semantic status
 */

import React from 'react';
import { cn } from '../../lib/utils';
import {
  getStatusBadgeClasses,
  type SemanticStatus,
} from '../../utils/color-utils';

/**
 * Status domain types
 */
export type StatusDomain =
  | 'request'
  | 'practice'
  | 'redemption'
  | 'cashback'
  | 'proposal'
  | 'semantic';

/**
 * Request status values
 */
export type RequestStatusValue =
  | 'draft'
  | 'pending_review'
  | 'in_progress'
  | 'info_required'
  | 'proposals_ready'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'expired';

/**
 * Practice status values
 */
export type PracticeStatusValue =
  | 'created'
  | 'in_progress'
  | 'pending_documents'
  | 'submitted_to_operator'
  | 'operator_processing'
  | 'completed'
  | 'cancelled'
  | 'failed';

/**
 * Redemption status values
 */
export type RedemptionStatusValue =
  | 'requested'
  | 'processing'
  | 'completed'
  | 'failed';

/**
 * Cashback status values
 */
export type CashbackStatusValue = 'available' | 'redeemed';

/**
 * Proposal status values
 */
export type ProposalStatusValue =
  | 'proposed'
  | 'accepted'
  | 'rejected'
  | 'expired';

/**
 * All possible status values
 */
export type StatusValue =
  | RequestStatusValue
  | PracticeStatusValue
  | RedemptionStatusValue
  | CashbackStatusValue
  | ProposalStatusValue
  | SemanticStatus
  | string;

/**
 * Badge size variants
 */
export type BadgeSize = 'sm' | 'md' | 'lg';

/**
 * StatusBadge props
 */
type StatusBadgeProps = {
  status: StatusValue;
  domain?: StatusDomain;
  size?: BadgeSize;
  className?: string;
  customLabel?: string;
};

/**
 * Status configuration with label and color
 */
type StatusConfig = {
  label: string;
  colorClass: string;
};

/**
 * Request status configurations
 */
const requestStatusConfig: Record<RequestStatusValue, StatusConfig> = {
  draft: {
    label: 'Bozza',
    colorClass: 'bg-muted text-muted-foreground',
  },
  pending_review: {
    label: 'Attesa Revisione',
    colorClass: 'bg-warning/15 text-warning-foreground',
  },
  in_progress: {
    label: 'In Lavorazione',
    colorClass: 'bg-info/10 text-info-foreground',
  },
  info_required: {
    label: 'Info Richieste',
    colorClass: 'bg-warning/20 text-warning-foreground',
  },
  proposals_ready: {
    label: 'Proposte Pronte',
    colorClass: 'bg-success/15 text-success-foreground',
  },
  completed: {
    label: 'Completata',
    colorClass: 'bg-success/15 text-success-foreground',
  },
  cancelled: {
    label: 'Annullata',
    colorClass: 'bg-muted text-muted-foreground',
  },
  rejected: {
    label: 'Respinta',
    colorClass: 'bg-destructive/15 text-destructive',
  },
  expired: {
    label: 'Scaduta',
    colorClass: 'bg-muted text-muted-foreground',
  },
};

/**
 * Practice status configurations
 */
const practiceStatusConfig: Record<PracticeStatusValue, StatusConfig> = {
  created: {
    label: 'Creata',
    colorClass: 'bg-info/15 text-info-foreground',
  },
  in_progress: {
    label: 'In Lavorazione',
    colorClass: 'bg-warning/15 text-warning-foreground',
  },
  pending_documents: {
    label: 'Attesa doc.',
    colorClass: 'bg-warning/20 text-warning-foreground',
  },
  submitted_to_operator: {
    label: "Inviata all'Operatore",
    colorClass: 'bg-primary/15 text-primary-strong',
  },
  operator_processing: {
    label: 'In Elaborazione',
    colorClass: 'bg-primary/20 text-primary-strong',
  },
  completed: {
    label: 'Completata',
    colorClass: 'bg-success/15 text-success-foreground',
  },
  cancelled: {
    label: 'Annullata',
    colorClass: 'bg-muted text-muted-foreground',
  },
  failed: {
    label: 'Fallita',
    colorClass: 'bg-destructive/15 text-destructive',
  },
};

/**
 * Redemption status configurations
 */
const redemptionStatusConfig: Record<RedemptionStatusValue, StatusConfig> = {
  requested: {
    label: 'Richiesto',
    colorClass: 'bg-chart-4/15 text-chart-4',
  },
  processing: {
    label: 'In elaborazione',
    colorClass: 'bg-warning/15 text-warning-foreground',
  },
  completed: {
    label: 'Completato',
    colorClass: 'bg-success/15 text-success-foreground',
  },
  failed: {
    label: 'Fallito',
    colorClass: 'bg-destructive/15 text-destructive',
  },
};

/**
 * Cashback status configurations
 */
const cashbackStatusConfig: Record<CashbackStatusValue, StatusConfig> = {
  available: {
    label: 'Disponibile',
    colorClass: 'bg-chart-1/15 text-chart-1',
  },
  redeemed: {
    label: 'Riscattato',
    colorClass: 'bg-success/15 text-success-foreground',
  },
};

/**
 * Proposal status configurations
 */
const proposalStatusConfig: Record<ProposalStatusValue, StatusConfig> = {
  proposed: {
    label: 'Proposta',
    colorClass: 'bg-info/15 text-info-foreground',
  },
  accepted: {
    label: 'Accettata',
    colorClass: 'bg-success/15 text-success-foreground',
  },
  rejected: {
    label: 'Rifiutata',
    colorClass: 'bg-destructive/15 text-destructive',
  },
  expired: {
    label: 'Scaduta',
    colorClass: 'bg-muted text-muted-foreground',
  },
};

/**
 * Semantic status configurations
 */
const semanticStatusConfig: Record<SemanticStatus, StatusConfig> = {
  success: { label: 'Successo', colorClass: getStatusBadgeClasses('success') },
  warning: {
    label: 'Attenzione',
    colorClass: getStatusBadgeClasses('warning'),
  },
  info: { label: 'Info', colorClass: getStatusBadgeClasses('info') },
  error: { label: 'Errore', colorClass: getStatusBadgeClasses('error') },
  neutral: { label: '-', colorClass: getStatusBadgeClasses('neutral') },
};

/**
 * Size class mappings
 */
const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
  lg: 'px-3 py-1 text-sm',
};

/**
 * Get status configuration based on domain
 */
const getStatusConfig = (
  status: StatusValue,
  domain: StatusDomain,
): StatusConfig => {
  switch (domain) {
    case 'request':
      return (
        requestStatusConfig[status as RequestStatusValue] || {
          label: status,
          colorClass: 'bg-muted text-muted-foreground',
        }
      );
    case 'practice':
      return (
        practiceStatusConfig[status as PracticeStatusValue] || {
          label: status,
          colorClass: 'bg-muted text-muted-foreground',
        }
      );
    case 'redemption':
      return (
        redemptionStatusConfig[status as RedemptionStatusValue] || {
          label: status,
          colorClass: 'bg-muted text-muted-foreground',
        }
      );
    case 'cashback':
      return (
        cashbackStatusConfig[status as CashbackStatusValue] || {
          label: status,
          colorClass: 'bg-muted text-muted-foreground',
        }
      );
    case 'proposal':
      return (
        proposalStatusConfig[status as ProposalStatusValue] || {
          label: status,
          colorClass: 'bg-muted text-muted-foreground',
        }
      );
    case 'semantic':
      return (
        semanticStatusConfig[status as SemanticStatus] || {
          label: status,
          colorClass: 'bg-muted text-muted-foreground',
        }
      );
    default:
      return {
        label: status as string,
        colorClass: 'bg-muted text-muted-foreground',
      };
  }
};

/**
 * StatusBadge component
 *
 * @example
 * // Request status
 * <StatusBadge status="pending_review" domain="request" />
 *
 * // Practice status
 * <StatusBadge status="completed" domain="practice" />
 *
 * // Redemption status
 * <StatusBadge status="processing" domain="redemption" />
 *
 * // With custom label
 * <StatusBadge status="success" domain="semantic" customLabel="Attivo" />
 *
 * // Large size
 * <StatusBadge status="completed" domain="practice" size="lg" />
 */
const StatusBadgeComponent = React.forwardRef<
  HTMLSpanElement,
  StatusBadgeProps
>(
  (
    { status, domain = 'semantic', size = 'md', className, customLabel },
    ref,
  ) => {
    const config = getStatusConfig(status, domain);
    const label = customLabel || config.label;

    return (
      <span
        ref={ref}
        className={cn(
          // Eurocat uppercase: the §9.6 tier matrix grants status chips to the
          // secondary face in *both* the customer and the sober register, so
          // this applies globally rather than behind a tier prop. Every size
          // below is 12px or 14px, clearing the dotted-face floor (§4.2a), and
          // the weight was already Medium.
          'inline-flex items-center rounded-pill font-display font-medium uppercase tracking-[0.08em]',
          sizeClasses[size],
          config.colorClass,
          className,
        )}
      >
        {label}
      </span>
    );
  },
);

StatusBadgeComponent.displayName = 'StatusBadge';

export const StatusBadge = StatusBadgeComponent;
